// helpdesk-frontend/src/components/common/Informator/Informator.js
/**
 * INFORMATOR — zamykalny box informacyjny z pamięcią zamknięcia.
 *
 * [brief właściciela 2026-09-05, zasada 3] „Informatory: mało, zamykalne z pamięcią, zdania po polsku,
 * konkretne." BYŁO ŹLE: e-teczka i panel działów miały kilkanaście STAŁYCH, niezamykalnych akapitów
 * tłumaczących model („Jedno pole… czyta je cała aplikacja…", „Każdy odczyt zostaje w dzienniku…"),
 * a żaden wspólny komponent podpowiedzi w components/ nie istniał (grep Informator|InfoBox|Callout: 0).
 * SKUTEK DLA UŻYTKOWNIKA: kadrowa czytała te same trzy akapity przy każdym otwarciu karty, a treść
 * właściwa spadała poniżej zgięcia ekranu.
 * NAPRAWA: jeden komponent — ✕ zamyka, zamknięcie zapamiętane w localStorage pod `informator:<id>`,
 * przy montowaniu box od razu NIE renderuje się, gdy był zamknięty (bez mrugnięcia).
 *
 * Pamięć jest best-effort: brak localStorage (prywatne okno, polityka przeglądarki) = informator
 * pokazuje się przy każdym wejściu i zamyka na czas życia komponentu — nigdy błąd ekranu.
 *
 * Styl wyłącznie tokenami design systemu (docs/DESIGN-SYSTEM-PODSTRON.md); akcent tylko jako lewy pasek 3px.
 *
 * PUŁAPKA DLA TESTÓW EKRANÓW: pamięć siedzi w localStorage, a jsdom NIE czyści go między testami w jednym
 * pliku — jeśli jeden test kliknie ✕, kolejne w tym samym pliku nie zobaczą treści informatora i zaczerwienią
 * się bez winy ekranu. W zestawach, które zamykają informator, dawaj `beforeEach(() => localStorage.clear())`.
 *
 * @param {string}  id         klucz pamięci (WYMAGANY) — stały dla danego miejsca na ekranie
 * @param {string}  [tytul]    krótki nagłówek boxu
 * @param {"info"|"uwaga"} [ton="info"]  „uwaga" = lewy pasek w kolorze ostrzeżenia
 * @param {node}    children   treść (zdania po polsku, konkretne)
 * @param {string}  [className]
 * @param {function} [onZamknij]  wołane po zamknięciu (np. licznik / telemetria); opcjonalne
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import styles from "./Informator.module.css";

export const PREFIX_PAMIECI = "informator:";

const kluczPamieci = (id) => (id ? PREFIX_PAMIECI + String(id) : null);

const odczytajZamkniecie = (id) => {
    const klucz = kluczPamieci(id);
    if (!klucz) return false;
    try {
        return localStorage.getItem(klucz) !== null;
    } catch {
        return false;
    }
};

const zapiszZamkniecie = (id) => {
    const klucz = kluczPamieci(id);
    if (!klucz) return;
    try {
        localStorage.setItem(klucz, new Date().toISOString());
    } catch {
        // brak storage — zamknięcie obowiązuje tylko do przeładowania
    }
};

const usunZamkniecie = (id) => {
    const klucz = kluczPamieci(id);
    if (!klucz) return;
    try {
        localStorage.removeItem(klucz);
    } catch {
        // brak storage — nic do usunięcia
    }
};

/**
 * Stan zamknięcia informatora o danym `id`.
 * @returns {[boolean, () => void, () => void]} [zamkniety, zamknij, przywroc]
 */
export const useInformatorZamkniety = (id) => {
    // Odczyt w inicjalizatorze stanu — box zamknięty NIE pojawia się nawet na jedną klatkę.
    const [zamkniety, setZamkniety] = useState(() => odczytajZamkniecie(id));

    // Zmiana `id` w tym samym komponencie (np. informator per zakładka) czyta pamięć na nowo.
    useEffect(() => {
        setZamkniety(odczytajZamkniecie(id));
    }, [id]);

    const zamknij = useCallback(() => {
        zapiszZamkniecie(id);
        setZamkniety(true);
    }, [id]);

    const przywroc = useCallback(() => {
        usunZamkniecie(id);
        setZamkniety(false);
    }, [id]);

    return [zamkniety, zamknij, przywroc];
};

const Informator = ({ id, tytul, ton = "info", children, className = "", onZamknij }) => {
    const [zamkniety, zamknij] = useInformatorZamkniety(id);

    const obsluzZamknij = useCallback(() => {
        zamknij();
        if (typeof onZamknij === "function") onZamknij(id);
    }, [zamknij, onZamknij, id]);

    if (zamkniety) return null;

    return (
        <aside
            className={`${styles.informator} ${ton === "uwaga" ? styles.uwaga : ""} ${className}`.trim()}
            role="note"
            data-informator={id}
        >
            <div className={styles.tresc}>
                {tytul && <p className={styles.tytul}>{tytul}</p>}
                <div className={styles.opis}>{children}</div>
            </div>
            <button
                type="button"
                className={styles.zamknij}
                onClick={obsluzZamknij}
                aria-label="Zamknij"
                title="Zamknij — nie pokazuj więcej"
            >
                <FiX size={14} />
            </button>
        </aside>
    );
};

export default Informator;
